import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { KnowledgeItem } from '@api/models/knowledge-item.model';
import { KnowledgeItemService } from '@api/services/knowledge-item.service';
import { IndexResponse } from '@api/models/response.model';
import { TableColumn, TableComponent } from '@ui/table/table.component';
import { CardComponent } from '@ui/card/card.component';
import { ButtonComponent } from '@ui/button/button.component';
import { ModalService } from '@ui/modal/modal.service';
import { NotificationService } from '@ui/notification/notification.service';
import { ConfirmService } from '@ui/confirm-modal/confirm.service';
import { LoadingService } from '@ui/app-layout/loading-service';
import { KnowledgeItemModalComponent } from './knowledge-item-modal.component';
import { KnowledgeItemPreviewModalComponent } from './knowledge-item-preview-modal.component';

const COMPONENT_LABELS: Record<string, string> = {
  rail: 'Rail',
  unit: 'Unit',
  general: 'Algemeen',
};

const TYPE_LABELS: Record<string, string> = {
  file: 'Bestand',
  link: 'YouTube',
};

@Component({
  selector: 'app-knowledge-items',
  standalone: true,
  imports: [CommonModule, TableComponent, CardComponent, ButtonComponent],
  template: `
    <div
      class="flex flex-col gap-4 h-[calc(100vh-theme(spacing.24))] overflow-hidden"
    >
      <ui-card>
        <div class="flex flex-wrap justify-between items-start gap-4">
          <div class="text-sm text-slate-600 max-w-3xl">
            <p>
              Beheer handleidingen en YouTube-video's voor de klanten-app.
              Video's worden in de app afgespeeld.               Items zonder fabrikant zijn zichtbaar voor alle klanten. Alleen
              een fabrikant geldt voor alle types van dat onderdeel; met een
              type alleen voor die combinatie.
            </p>
          </div>
          <ui-button
            [icon]="'add'"
            (onClick)="openCreate()"
            class="shrink-0"
          >
            Item toevoegen
          </ui-button>
        </div>
      </ui-card>

      <div class="h-full overflow-hidden">
        <ui-table
          [columns]="columns"
          [data]="items()"
          [loading]="loading()"
          [showPagination]="true"
          [meta]="meta()"
          [bordered]="false"
          emptyMessage="Nog geen kennisbank-items"
          (onPageChange)="onPageChange($event)"
        ></ui-table>
      </div>
    </div>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KnowledgeItemsComponent {
  private knowledgeItemService = inject(KnowledgeItemService);
  private modalService = inject(ModalService);
  private notificationService = inject(NotificationService);
  private confirmService = inject(ConfirmService);
  private loadingService = inject(LoadingService);

  items = signal<KnowledgeItem[]>([]);
  loading = signal(false);
  meta = signal<IndexResponse<KnowledgeItem>['meta'] | null>(null);

  columns: TableColumn[] = [
    { key: 'title', label: 'Titel' },
    {
      key: 'type',
      label: 'Type',
      format: (v: string) => TYPE_LABELS[v] ?? v,
    },
    {
      key: 'component',
      label: 'Onderdeel',
      format: (v: string) => COMPONENT_LABELS[v] ?? v,
    },
    {
      key: 'manufacturer',
      label: 'Fabrikant',
      format: (v: string | null) => v || 'Alle klanten',
    },
    {
      key: 'product_type',
      label: 'Type',
      format: (v: string | null, row?: KnowledgeItem) =>
        v || (row?.manufacturer ? 'Alle types' : '—'),
    },
    {
      key: 'created_at',
      label: 'Toegevoegd',
      align: 'right',
      format: (v: string) =>
        new Date(v).toLocaleString('nl-NL', {
          dateStyle: 'short',
          timeStyle: 'short',
        }),
    },
    {
      key: '_actions',
      label: '',
      align: 'right',
      width: '140px',
      actions: [
        {
          label: 'Openen',
          icon: 'download',
          hideLabel: true,
          variant: 'secondary',
          onClick: (row: KnowledgeItem) => this.openItem(row),
        },
        {
          label: 'Bewerken',
          icon: 'edit',
          hideLabel: true,
          variant: 'secondary',
          onClick: (row: KnowledgeItem) => this.openEdit(row),
        },
        {
          label: 'Verwijderen',
          icon: 'delete',
          hideLabel: true,
          variant: 'danger',
          onClick: (row: KnowledgeItem) => this.confirmDelete(row),
        },
      ],
    },
  ];

  constructor() {
    this.loadPage(1);
  }

  loadPage(page: number) {
    this.loading.set(true);
    this.knowledgeItemService.getItems(page).subscribe({
      next: (res) => {
        this.items.set(res.data);
        this.meta.set(res.meta);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.notificationService.error('Kennisbank laden mislukt.');
      },
    });
  }

  onPageChange(page: number) {
    this.loadPage(page);
  }

  openCreate() {
    const ref = this.modalService.open(KnowledgeItemModalComponent, {
      data: { item: null },
    });
    ref.onClose.subscribe(() => this.loadPage(this.meta()?.current_page ?? 1));
  }

  openEdit(item: KnowledgeItem) {
    const ref = this.modalService.open(KnowledgeItemModalComponent, {
      data: { item },
    });
    ref.onClose.subscribe(() => this.loadPage(this.meta()?.current_page ?? 1));
  }

  openItem(item: KnowledgeItem) {
    if (item.type === 'link') {
      if (!item.embed_url) {
        this.notificationService.error('Deze YouTube-video kan niet worden afgespeeld.');
        return;
      }
      this.modalService.open(KnowledgeItemPreviewModalComponent, {
        data: { item },
      });
      return;
    }
    if (item.type !== 'file') {
      return;
    }
    this.loadingService.show();
    this.knowledgeItemService.download(item.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = item.original_filename || item.title;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        this.loadingService.hide();
      },
      error: () => {
        this.loadingService.hide();
        this.notificationService.error('Download mislukt.');
      },
    });
  }

  confirmDelete(item: KnowledgeItem) {
    this.confirmService
      .confirm({
        title: 'Item verwijderen',
        message: `Weet u zeker dat u "${item.title}" wilt verwijderen?`,
        confirmText: 'Verwijderen',
        variant: 'danger',
      })
      .then((ok) => {
        if (!ok) {
          return;
        }
        this.knowledgeItemService.delete(item.id).subscribe({
          next: () => {
            this.notificationService.success('Item verwijderd.');
            this.loadPage(this.meta()?.current_page ?? 1);
          },
          error: () => {
            this.notificationService.error('Verwijderen mislukt.');
          },
        });
      });
  }
}
